import { useCallback, useEffect, useMemo, useRef } from 'react';
import isEqual from 'lodash.isequal';

import type { StaticFieldProps } from '../../types/common';
import { getFilteredSelectOptions } from '../utils/getFilteredSelectOptions';
import { FieldMeta } from '../common/FieldMeta';
import SelectFieldOptions from '../common/SelectFieldOptions';
import autoScrollToNextField from '../utils/autoScrollToNextField';

import type { MultiSelectField } from './types';
import { getNewSelectValuesOnOptionsChange } from './getNewSelectValuesOnOptionsChange';

export function MultiSelectFieldElement({
  path,
  field,
  value,
  onValueChange,
  errors,
  isTouched,
  allValues,
  allErrors,
  sectionKey,
  propagateFieldLabelToParent,
}: StaticFieldProps<MultiSelectField>) {
  const ref = useRef<HTMLDivElement>();
  const options = useMemo(
    () =>
      getFilteredSelectOptions({
        options: field.options,
        sectionKey,
        allValues,
        allErrors,
      }),
    [allErrors, allValues, field.options, sectionKey]
  );
  const safeValue = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const includesSelectAll = useMemo(() => options.some((o) => o.selectAll), [options]);
  const isAllSelected = useMemo(
    () => safeValue.length === options.filter((o) => !o.selectAll).length,
    [options, safeValue.length]
  );
  const optionsValue = useMemo(() => {
    if (isAllSelected) {
      return ['all', ...safeValue];
    }
    return safeValue;
  }, [isAllSelected, safeValue]);

  useEffect(() => {
    onValueChange((prev = []) => {
      if (prev?.length > 0) return prev;

      const defaultValues = field.defaultValue as string[];
      return defaultValues?.length > 0 ? defaultValues : prev;
    }, true);
  }, []);

  useEffect(() => {
    const newValues = getNewSelectValuesOnOptionsChange({
      options,
      value: safeValue,
    });

    if (isEqual(newValues, safeValue)) {
      return;
    }

    return onValueChange(newValues);
  }, [
    options,
    includesSelectAll,
    onValueChange,
    safeValue,
    propagateFieldLabelToParent,
    isAllSelected,
    field.key,
  ]);

  const isPropagateFieldLabelToParentFunction = typeof propagateFieldLabelToParent === 'function';

  useEffect(() => {
    if (isPropagateFieldLabelToParentFunction) {
      const newLabel = isAllSelected
        ? options.find((o) => o.selectAll)?.label
        : options.filter((o) => safeValue.includes(o.value)).map((o) => o.label);
      propagateFieldLabelToParent(path, newLabel);
    }

    return () => {
      if (isPropagateFieldLabelToParentFunction) {
        propagateFieldLabelToParent(path, undefined);
      }
    };
  }, [
    propagateFieldLabelToParent,
    isPropagateFieldLabelToParentFunction,
    path.join('.'),
    isAllSelected,
    options,
    safeValue,
  ]);

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;
      const optionValue = target.value;
      const allOptionsValues = options.map((option) => option.value).filter((v) => v !== 'all');

      onValueChange((prev = []) => {
        if (!Array.isArray(prev)) {
          return [optionValue];
        }

        if (optionValue === 'all') {
          if (!isAllSelected) {
            autoScrollToNextField(field?.scrollToNextSection, ref?.current);
          }

          return isAllSelected ? [] : allOptionsValues;
        }

        if (prev.includes(optionValue)) {
          return prev.filter((v) => v !== optionValue);
        }

        const newValue = [...prev, optionValue];

        return newValue;
      });
    },
    [options, onValueChange, field?.scrollToNextSection, isAllSelected]
  );

  if (options.length === 0) {
    return null;
  }

  return (
    <div ref={ref}>
      <FieldMeta field={field} errors={errors} isTouched={isTouched} />
      <SelectFieldOptions
        field={field}
        options={options}
        value={optionsValue}
        onChange={handleOnChange}
      />
    </div>
  );
}
